import { AwardItem, CampaignAwards, AWARD_TYPE, CERT_TYPE } from '@yandex-int/k-common';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { createSelector } from 'reselect';

import { getCourseIdFromProps } from '../../utils/store/get-from-props';

import { AwardsStoreState, ReduxAwardsStore } from './typings';

const storeSelector = (storeState: ReduxAwardsStore): AwardsStoreState => storeState.awards;

export const getCampaignAwards = createSelector(storeSelector, (store) => store.awards);

export const getActiveAwards = createSelector(getCampaignAwards, (campaignAwardItems) => {
    return campaignAwardItems.reduce((result: AwardItem[], campaignAwards: CampaignAwards) => {
        const nowMs = new Date().getTime();
        const items = campaignAwards.award_items.filter(
            (item) => new Date(item.award.deprecation_dt).getTime() > nowMs
        );

        return result.concat(items);
    }, []);
});

export const getAvailableCertificates = createSelector(getActiveAwards, (activeAwards) => {
    return activeAwards.filter((award) => {
        return Boolean(award.award.award_type === AWARD_TYPE.OLYMP_STUDENT_CERTIFICATE && award.certificates?.length);
    });
});

export const getStudentCertificate = createSelector(getAvailableCertificates, (availableCertificates) => {
    return availableCertificates.find((award) => award.award.view_details?.type === CERT_TYPE.CERT_FOR_ONE_LESSON);
});

export const getAvailableCertificateAwards = createSelector(getAvailableCertificates, (availableAwards) => {
    return availableAwards.filter((award) => award.award.view_details?.type === CERT_TYPE.AWARD_CERT);
});

export const getAvailableLinkAwards = createSelector(getActiveAwards, (activeAwards) => {
    return activeAwards.filter((award) => award.award.award_type === AWARD_TYPE.LINK);
});

export const getAvailableAwards = createSelector(
    [getAvailableCertificateAwards, getAvailableLinkAwards],
    (availableCertificateAwards, availableLinkAwards) => {
        return [...availableCertificateAwards, ...availableLinkAwards];
    }
);

export const getIsAwardsLoading = createSelector(
    storeSelector,
    (store) => store.awardsLoadingStatus === LOADING_STATUSES.LOADING
);

export const getHasAwards = createSelector([getAvailableCertificateAwards], (awards) => awards.length > 0);

export const getStudentsCampaignAwardsByCourse = createSelector(
    [storeSelector, getCourseIdFromProps],
    (store, courseId) => {
        if (courseId && store?.studentsAwards?.[courseId]) {
            return store.studentsAwards[courseId];
        }

        return [];
    }
);

export const getStudentsAwardItems = createSelector([getStudentsCampaignAwardsByCourse], (campaignAwards) => {
    return campaignAwards.reduce((result: AwardItem[], campaignAward: CampaignAwards) => {
        const items = campaignAward.award_items.filter(
            (item) => item.award.award_type === AWARD_TYPE.OLYMP_STUDENT_CERTIFICATE
        );

        return result.concat(items);
    }, []);
});

export const getStudentsAwardsCertificates = createSelector([getStudentsAwardItems], (awardItems) =>
    awardItems.filter((awardItem) => awardItem.award.view_details?.type === CERT_TYPE.AWARD_CERT)
);

export const getStudentsLessonsCertificates = createSelector([getStudentsAwardItems], (awardItems) =>
    awardItems.filter((awardItem) => awardItem.award.view_details?.type === CERT_TYPE.CERT_FOR_ONE_LESSON)
);
